import { supabase } from '../../../shared/lib/supabase';

export interface StorageUsageByCategory {
  category: string;
  label: string;
  bytes: number;
  percentage: number;
  color: string;
}

export interface StorageUsageSummary {
  totalBytes: number;
  totalGB: number;
  categories: StorageUsageByCategory[];
}

/**
 * Service to calculate storage usage by organization
 * 
 * Note: Storage calculation for highlights and tactics images uses the same filtering logic
 * as the match assets screen (highlightsTacticsService):
 * - Tournament access AND Team access (both required)
 * - Only matches in accessible tournaments that also have at least one accessible team
 * - Only completed highlights and uploaded tactics images are counted
 * 
 * This ensures consistency between what orgs see in match assets and what's counted in storage usage.
 */
export const storageUsageService = {
  /**
   * Get storage usage breakdown for an organization
   * 
   * Calculates storage for:
   * 1. Video Library Master Files - Direct from videos table (size_bytes)
   * 2. Thumbnails - Actual sizes from video_processing_jobs.payload.generatedAssets[].sizeBytes
   * 3. Highlights - Only for matches in accessible tournaments AND with accessible teams (file_size_bytes from DB)
   * 4. Tactics Images - Only for matches in accessible tournaments AND with accessible teams (file_size_bytes from DB)
   * 
   * @param orgId - Organization ID
   * @param dateFrom - Optional start date for filtering (ISO string)
   * @param dateTo - Optional end date for filtering (ISO string)
   */
  getStorageUsageByOrg: async (
    orgId: string,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Promise<StorageUsageSummary> => {
    try {
      // 1. Video Library Master Files (videos table - only R2/storage videos)
      let videosQuery = supabase
        .from('videos')
        .select('id, size_bytes')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .in('source_type', ['cloudflare_r2', 'supabase_storage', 'local']);
      
      // Apply date filter if provided (filter by created_at for consistency with other entities)
      if (dateFrom) {
        videosQuery = videosQuery.gte('created_at', dateFrom);
      }
      if (dateTo) {
        // Add one day to include the entire end date
        const endDate = new Date(dateTo);
        endDate.setDate(endDate.getDate() + 1);
        videosQuery = videosQuery.lt('created_at', endDate.toISOString());
      }
      
      const { data: videos, error: videosError } = await videosQuery;

      if (videosError) {
        console.error('Error fetching videos:', videosError);
      }

      const videoMasterBytes = videos?.reduce((sum, video) => {
        return sum + (video.size_bytes || 0);
      }, 0) || 0;

      // 2. Thumbnails - get actual sizes from video_processing_jobs
      // Thumbnail sizes are stored in job.payload.generatedAssets[].sizeBytes
      // Query through videos table to respect RLS policies
      let thumbnailBytes = 0;
      if (videos && videos.length > 0) {
        const videoIds = videos.map(v => v.id);
        
        // Query through videos table to respect RLS, then get related processing jobs
        let thumbnailJobsQuery = supabase
          .from('videos')
          .select(`
            id,
            video_processing_jobs!inner (
              payload,
              created_at
            )
          `)
          .in('id', videoIds)
          .eq('video_processing_jobs.job_type', 'thumbnail')
          .eq('video_processing_jobs.status', 'succeeded')
          .eq('video_processing_jobs.is_active', true);
        
        // Apply date filter to thumbnail jobs if provided
        if (dateFrom) {
          thumbnailJobsQuery = thumbnailJobsQuery.gte('video_processing_jobs.created_at', dateFrom);
        }
        if (dateTo) {
          const endDate = new Date(dateTo);
          endDate.setDate(endDate.getDate() + 1);
          thumbnailJobsQuery = thumbnailJobsQuery.lt('video_processing_jobs.created_at', endDate.toISOString());
        }
        
        const { data: videosWithJobs, error: thumbnailJobsError } = await thumbnailJobsQuery;

        if (thumbnailJobsError) {
          console.error('Error fetching thumbnail jobs:', thumbnailJobsError);
        } else if (videosWithJobs) {
          // Sum up sizes from generatedAssets
          videosWithJobs.forEach((video: any) => {
            const jobs = Array.isArray(video.video_processing_jobs) 
              ? video.video_processing_jobs 
              : [video.video_processing_jobs];
            
            jobs.forEach((job: any) => {
              const payload = typeof job.payload === 'string' ? JSON.parse(job.payload) : job.payload;
              const generatedAssets = Array.isArray(payload?.generatedAssets) ? payload.generatedAssets : [];
              generatedAssets.forEach((asset: any) => {
                if (asset?.sizeBytes && typeof asset.sizeBytes === 'number') {
                  thumbnailBytes += asset.sizeBytes;
                }
              });
            });
          });
        }
      }

      // Get accessible tournament and team IDs for the org (used by both highlights and tactics)
      const { data: tournamentAccess, error: tournamentError } = await supabase
        .from('tournament_access')
        .select('tournament_id')
        .eq('organisation_id', orgId)
        .eq('is_active', true);

      if (tournamentError) {
        console.error('[StorageUsage] Error fetching tournament access:', tournamentError);
      }

      const tournamentIdsArray = tournamentAccess?.map(ta => ta.tournament_id) || [];
      const tournamentIds = new Set(tournamentIdsArray);
      console.log('[StorageUsage] Tournament access:', { count: tournamentIds.size });

      // 3. Highlights (video_highlights table)
      // Use the same filtering logic as match assets screen:
      // - Tournament access AND Team access (both required)
      // - Only matches in accessible tournaments that also have at least one accessible team
      // File sizes are stored in the database (file_size_bytes column)
      let highlightBytes = 0;
      
      console.log('[StorageUsage] Calculating highlights storage for org:', orgId);

      // Step 1: Get accessible team IDs for the org
      const { data: teamAccess, error: teamAccessError } = await supabase
        .from('team_access')
        .select('team_id')
        .eq('organisation_id', orgId)
        .eq('active', true);

      if (teamAccessError) {
        console.error('[StorageUsage] Error fetching team access:', teamAccessError);
      }

      const accessibleTeamIds = teamAccess?.map(ta => ta.team_id) || [];
      const teamIds = new Set(accessibleTeamIds);
      console.log('[StorageUsage] Team access:', { count: teamIds.size });

      // Step 2: Get accessible matches using AND logic (tournament AND team access)
      // This matches the logic in highlightsTacticsService.getTournamentsWithMatches()
      let accessibleMatchIds: string[] = [];

      if (tournamentIdsArray.length > 0 && accessibleTeamIds.length > 0) {
        // Step 2a: Get matches from accessible tournaments only
        const { data: matches, error: matchesError } = await supabase
          .from('matches')
          .select('id, tournament_id, home_team_id, away_team_id')
          .in('tournament_id', tournamentIdsArray)
          .eq('active', true);

        if (matchesError) {
          console.error('[StorageUsage] Error fetching matches:', matchesError);
        } else if (matches && matches.length > 0) {
          // Step 2b: Filter matches by team access (home or away team must be accessible)
          // This implements AND logic: tournament access AND team access
          const accessibleMatches = matches.filter(
            (match: any) =>
              accessibleTeamIds.includes(match.home_team_id) ||
              accessibleTeamIds.includes(match.away_team_id)
          );

          accessibleMatchIds = accessibleMatches.map((m: any) => m.id);
          console.log('[StorageUsage] Accessible matches (tournament AND team access):', {
            totalMatchesInTournaments: matches.length,
            accessibleMatchesCount: accessibleMatchIds.length,
          });
        }
      } else {
        console.log('[StorageUsage] No tournament or team access - no highlights accessible');
      }

      // Step 3: Get completed and stitched highlights for accessible matches only
      if (accessibleMatchIds.length > 0) {
        let highlightsQuery = supabase
          .from('video_highlights')
          .select('id, match_id, file_size_bytes, enhanced_file_size_bytes, created_at')
          .in('match_id', accessibleMatchIds)
          .in('status', ['stitched', 'completed'])
          .eq('is_active', true);
        
        // Apply date filter if provided (filter by created_at)
        if (dateFrom) {
          highlightsQuery = highlightsQuery.gte('created_at', dateFrom);
        }
        if (dateTo) {
          const endDate = new Date(dateTo);
          endDate.setDate(endDate.getDate() + 1);
          highlightsQuery = highlightsQuery.lt('created_at', endDate.toISOString());
        }
        
        const { data: accessibleHighlights, error: highlightsError } = await highlightsQuery;

        if (highlightsError) {
          console.error('[StorageUsage] Error fetching highlights:', highlightsError);
        } else if (accessibleHighlights && accessibleHighlights.length > 0) {
          // Step 4: Sum up file sizes from database
          // Prefer enhanced_file_size_bytes if available, fallback to file_size_bytes
          highlightBytes = accessibleHighlights.reduce((sum, highlight: any) => {
            const size = highlight.enhanced_file_size_bytes ?? highlight.file_size_bytes ?? 0;
            return sum + size;
          }, 0);

          const highlightsWithSize = accessibleHighlights.filter((h: any) => {
            const size = h.enhanced_file_size_bytes ?? h.file_size_bytes;
            return size && size > 0;
          });
          const highlightsWithoutSize = accessibleHighlights.filter((h: any) => {
            const size = h.enhanced_file_size_bytes ?? h.file_size_bytes;
            return !size || size === 0;
          });

          console.log('[StorageUsage] Highlights storage calculation:', {
            accessibleHighlights: accessibleHighlights.length,
            highlightsWithSize: highlightsWithSize.length,
            highlightsWithoutSize: highlightsWithoutSize.length,
            totalBytes: highlightBytes,
            totalGB: (highlightBytes / (1024 * 1024 * 1024)).toFixed(2),
          });

          if (highlightsWithoutSize.length > 0) {
            console.warn('[StorageUsage] Some highlights are missing file_size_bytes (will be 0 until backfilled):', {
              count: highlightsWithoutSize.length,
              sample: highlightsWithoutSize.slice(0, 3).map(h => ({
                id: h.id,
                match_id: h.match_id,
              })),
            });
          }
        } else {
          console.log('[StorageUsage] No highlights found for accessible matches');
        }
      } else {
        console.log('[StorageUsage] No accessible matches - no highlights to calculate');
      }

      // 4. Tactics Images (tactics_images table)
      // Use the same filtering logic as match assets screen:
      // - Tournament access AND Team access (both required)
      // - Reuse the same accessibleMatchIds from highlights (same AND logic)
      let tacticsBytes = 0;
      
      console.log('[StorageUsage] Calculating tactics images storage for org:', orgId);

      // Reuse accessibleMatchIds from highlights calculation (same tournament AND team access logic)
      if (accessibleMatchIds.length > 0) {
        let tacticsQuery = supabase
          .from('tactics_images')
          .select('id, match_id, file_size_bytes, created_at')
          .in('match_id', accessibleMatchIds)
          .eq('status', 'uploaded')
          .eq('is_active', true);
        
        // Apply date filter if provided (filter by created_at)
        if (dateFrom) {
          tacticsQuery = tacticsQuery.gte('created_at', dateFrom);
        }
        if (dateTo) {
          const endDate = new Date(dateTo);
          endDate.setDate(endDate.getDate() + 1);
          tacticsQuery = tacticsQuery.lt('created_at', endDate.toISOString());
        }
        
        const { data: accessibleTactics, error: tacticsError } = await tacticsQuery;

        if (tacticsError) {
          console.error('[StorageUsage] Error fetching tactics images:', tacticsError);
        } else if (accessibleTactics && accessibleTactics.length > 0) {
          tacticsBytes = accessibleTactics.reduce((sum, img) => {
            return sum + (img.file_size_bytes || 0);
          }, 0);

          console.log('[StorageUsage] Tactics images storage calculation:', {
            accessibleTactics: accessibleTactics.length,
            totalBytes: tacticsBytes,
            totalMB: (tacticsBytes / (1024 * 1024)).toFixed(2),
          });
        } else {
          console.log('[StorageUsage] No tactics images found for accessible matches');
        }
      } else {
        console.log('[StorageUsage] No accessible matches - no tactics images to calculate');
      }

      // Calculate totals
      const totalBytes = videoMasterBytes + thumbnailBytes + highlightBytes + tacticsBytes;
      const totalGB = totalBytes / (1024 * 1024 * 1024);

      // Final summary log
      console.log('[StorageUsage] Final summary:', {
        videoMasterBytes,
        thumbnailBytes,
        highlightBytes,
        tacticsBytes,
        totalBytes,
        totalGB: totalGB.toFixed(2),
      });

      // Calculate percentages
      // Always show all 4 categories, even when value is zero
      const categories: StorageUsageByCategory[] = [
        {
          category: 'videos',
          label: 'Video Library Master Files',
          bytes: videoMasterBytes,
          percentage: totalBytes > 0 ? (videoMasterBytes / totalBytes) * 100 : 0,
          color: '#3B82F6', // blue
        },
        {
          category: 'thumbnails',
          label: 'Thumbnails',
          bytes: thumbnailBytes,
          percentage: totalBytes > 0 ? (thumbnailBytes / totalBytes) * 100 : 0,
          color: '#10B981', // green
        },
        {
          category: 'highlights',
          label: 'Highlights',
          bytes: highlightBytes,
          percentage: totalBytes > 0 ? (highlightBytes / totalBytes) * 100 : 0,
          color: '#F59E0B', // amber
        },
        {
          category: 'tactics',
          label: 'Tactics Images',
          bytes: tacticsBytes,
          percentage: totalBytes > 0 ? (tacticsBytes / totalBytes) * 100 : 0,
          color: '#EF4444', // red
        },
      ];

      return {
        totalBytes,
        totalGB,
        categories,
      };
    } catch (error) {
      console.error('Error calculating storage usage:', error);
      return {
        totalBytes: 0,
        totalGB: 0,
        categories: [],
      };
    }
  },
};

